import { useContext, useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../context/auth-context';
import {
  Box,
  Typography,
  Card,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Drawer,
  Tooltip,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PaymentsIcon from '@mui/icons-material/Payments';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PaidIcon from '@mui/icons-material/Paid';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import Sidebar from '../components/Sidebar';
import { getVacationRequests, fetchUsers, getPayrollRecords } from '../api/services';
import { format, parse, isValid } from 'date-fns';

// Same palette as the employee homepage — one primary accent (teal), with a
// couple of muted tints used only for small icon badges, never full cards.
const TEAL = '#009db9';
const TEAL_DARK = '#00707f';
const AMBER = '#b26a00';
const AMBER_BG = 'rgba(245,124,0,0.1)';
const VIOLET = '#5738d1';
const VIOLET_BG = 'rgba(124,92,255,0.1)';
const TEAL_BG = 'rgba(0,157,185,0.1)';

const QUICK_ACTIONS = [
  { text: 'Add Earnings', path: '/earnings-management', icon: PaidIcon },
  { text: 'Create User', path: '/user-management', icon: PersonAddIcon },
  { text: 'View Vacations', path: '/vacation-management', icon: BeachAccessIcon },
];

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const fmtRequestDate = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (isNaN(d)) return 'N/A';
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const statusColor = (status) => {
  switch (status) {
    case 'PENDING':
      return { color: AMBER, bg: AMBER_BG };
    case 'APPROVED':
      return { color: '#2e7d32', bg: 'rgba(46,125,50,0.1)' };
    case 'REJECTED':
      return { color: '#c62828', bg: 'rgba(198,40,40,0.1)' };
    default:
      return { color: 'text.secondary', bg: 'rgba(0,0,0,0.06)' };
  }
};

const StatCard = ({ label, value, icon: Icon, color, bg }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: '18px',
      border: '1px solid rgba(0,0,0,0.08)',
      boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
      p: { xs: 2.5, sm: 3 },
      height: '100%',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
      <Box sx={{ bgcolor: bg, borderRadius: '10px', p: 0.9, display: 'flex' }}>
        <Icon sx={{ color, fontSize: '1.2rem' }} />
      </Box>
    </Box>
    <Typography sx={{ fontWeight: 'bold', color: TEAL_DARK, fontSize: { xs: '1.8rem', sm: '2.1rem' } }}>
      {value}
    </Typography>
  </Card>
);

const AdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const [dashboardData, setDashboardData] = useState(null);
  const [pendingRequestsList, setPendingRequestsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [mobileOpen, setMobileOpen] = useState(false);

  // Fetches dashboard data — identical calls/order/params to the original.
  // The only addition is keeping the actual pending-request objects (not
  // just their count) in state, for the new table below. No new API calls.
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const vacationResponse = await getVacationRequests('Pending', 0, 1000);
        let pendingRequests = Array.isArray(vacationResponse) ? vacationResponse.length : 0;
        let pendingList = Array.isArray(vacationResponse) ? vacationResponse : [];

        if (pendingRequests === 0) {
          const allResponse = await getVacationRequests('All', 0, 1000);
          const filtered = Array.isArray(allResponse)
            ? allResponse.filter((req) => req.status === 'PENDING')
            : [];
          pendingRequests = filtered.length;
          pendingList = filtered;
        }

        const usersResponse = await fetchUsers();
        const totalUsers = Array.isArray(usersResponse) && usersResponse[0]?.activeUsers ? usersResponse[0].activeUsers.length : 0;

        const payrollResponse = await getPayrollRecords(0, 1);
        let currentPaymentDate = Array.isArray(payrollResponse) && payrollResponse.length > 0
          ? payrollResponse[0].currentPaymentDate || 'N/A'
          : 'N/A';

        // Format the date if it's valid and not 'N/A'
        if (currentPaymentDate !== 'N/A') {
          try {
            const parsedDate = parse(currentPaymentDate, 'yyyy-MM-dd', new Date());
            if (isValid(parsedDate)) {
              currentPaymentDate = format(parsedDate, 'dd/MM/yyyy');
            } else {
              currentPaymentDate = 'Invalid Date';
            }
          } catch (error) {
            currentPaymentDate = 'Invalid Date';
          }
        }

        setDashboardData({
          pendingRequests,
          currentPayment: currentPaymentDate,
          totalUsers,
        });
        setPendingRequestsList(pendingList);
      } catch (error) {
        setSnackbarMessage(
          error.response?.status === 404
            ? 'No vacation requests found. Check backend user data.'
            : error.message || 'Error fetching dashboard data'
        );
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setDashboardData({
          pendingRequests: 0,
          currentPayment: 'N/A',
          totalUsers: 0,
        });
        setPendingRequestsList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f6f7' }}>
      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          width: '100%',
          flexGrow: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Sidebar />
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            p: { xs: 2, md: 4 },
            pt: { xs: '10px', md: '120px' },
            minHeight: '100vh',
            ml: { md: '20px' },
            pr: { xs: 1, md: 3 },
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              position: 'sticky',
              top: { xs: '64px', sm: '80px' },
              zIndex: 1100,
              mb: 2,
            }}
          >
            <Tooltip title="Menu" arrow>
              <IconButton
                onClick={handleDrawerToggle}
                sx={{
                  background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
                  color: '#fff',
                  borderRadius: '12px',
                  p: 1.2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '&:hover': {
                    transform: 'rotate(90deg)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                  },
                  transition: 'all 0.3s ease',
                }}
                aria-label="toggle sidebar"
              >
                <MenuIcon sx={{ fontSize: '1.8rem' }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Every section below is a child of this single flex column — one
              spacing system, consistent gap, no per-card margin guesswork. */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 4, md: 5 } }}>

          {/* Hero */}
          <Card
            sx={{
              borderRadius: '24px',
              background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
              px: { xs: 3, md: 5 },
              py: { xs: 3.5, md: 4.5 },
              boxShadow: '0 12px 30px rgba(0,112,127,0.25)',
            }}
          >
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', mb: 0.5 }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
            </Typography>
            <Typography sx={{ fontWeight: 'bold', color: '#fff', fontSize: { xs: '1.6rem', md: '2.1rem' } }}>
              {getGreeting()}, {user?.name || 'Admin'}
            </Typography>
          </Card>

          {/* At a glance */}
          <Box>
            <Typography sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5 }}>
              At a glance
            </Typography>

            {loading || !dashboardData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: TEAL }} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1.25 }}>
                <Box sx={{ width: { xs: '100%', sm: '33.333%' }, boxSizing: 'border-box', p: 1.25 }}>
                  <StatCard label="Pending Requests" value={dashboardData.pendingRequests || 0} icon={PendingActionsIcon} color={AMBER} bg={AMBER_BG} />
                </Box>
                <Box sx={{ width: { xs: '100%', sm: '33.333%' }, boxSizing: 'border-box', p: 1.25 }}>
                  <StatCard label="Current Payment" value={dashboardData.currentPayment || 'N/A'} icon={PaymentsIcon} color={TEAL} bg={TEAL_BG} />
                </Box>
                <Box sx={{ width: { xs: '100%', sm: '33.333%' }, boxSizing: 'border-box', p: 1.25 }}>
                  <StatCard label="Total Active Users" value={dashboardData.totalUsers || 0} icon={GroupsIcon} color={VIOLET} bg={VIOLET_BG} />
                </Box>
              </Box>
            )}
          </Box>

          {/* Quick actions */}
          <Box>
            <Typography sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5 }}>
              Quick actions
            </Typography>
            <Card
              variant="outlined"
              sx={{
                borderRadius: '20px',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                p: { xs: 2.5, md: 3 },
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1 }}>
                {QUICK_ACTIONS.map(({ text, path, icon: Icon }) => (
                  <Box key={text} sx={{ width: { xs: '100%', sm: '33.333%' }, boxSizing: 'border-box', p: 1 }}>
                    <Button
                      component={RouterLink}
                      to={path}
                      fullWidth
                      startIcon={<Icon />}
                      sx={{
                        bgcolor: '#f5f6f7',
                        color: TEAL_DARK,
                        borderRadius: '14px',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        px: 2.5,
                        py: 1.6,
                        justifyContent: 'flex-start',
                        transition: 'all 0.2s ease',
                        '&:hover': { bgcolor: TEAL, color: '#fff', transform: 'translateY(-2px)' },
                      }}
                    >
                      {text}
                    </Button>
                  </Box>
                ))}
              </Box>
            </Card>
          </Box>

          {/* Recent pending requests — reuses the list already fetched above */}
          <Box>
            <Typography sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5 }}>
              Recent pending requests
          </Typography>
          <Card
            variant="outlined"
            sx={{
              borderRadius: '20px',
              border: '1px solid rgba(0,0,0,0.08)',
              boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
              overflow: 'hidden',
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: TEAL }} />
              </Box>
            ) : pendingRequestsList.length === 0 ? (
              <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4, fontSize: '0.9rem' }}>
                No pending requests right now.
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 340 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '45%', fontWeight: 'bold', bgcolor: '#f5f6f7', color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Employee
                      </TableCell>
                      <TableCell sx={{ width: '30%', fontWeight: 'bold', bgcolor: '#f5f6f7', color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Requested
                      </TableCell>
                      <TableCell sx={{ width: '25%', fontWeight: 'bold', bgcolor: '#f5f6f7', color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingRequestsList.map((req, idx) => {
                      const sc = statusColor(req.status);
                      return (
                        <TableRow
                          key={req.id ?? idx}
                          sx={{
                            '&:nth-of-type(odd)': { bgcolor: '#fafbfc' },
                            '&:hover': { bgcolor: TEAL_BG },
                          }}
                        >
                          <TableCell sx={{ width: '45%', fontSize: '0.85rem' }}>
                            {/* Field name not confirmed — adjust to match your VacationRequest DTO */}
                            {req.employeeName || req.userName || req.requesterName || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ width: '30%', fontSize: '0.85rem' }}>
                            {fmtRequestDate(req.startDate || req.requestDate || req.createdAt)}
                          </TableCell>
                          <TableCell sx={{ width: '25%' }}>
                            <Chip
                              label={req.status || 'PENDING'}
                              size="small"
                              sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 'bold', fontSize: '0.7rem' }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
          </Box>

          </Box>
        </Box>
      </Box>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: 1100,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 250,
            top: { xs: '64px', sm: '80px' },
            height: 'calc(100% - 64px)',
            borderRight: 'none',
          },
        }}
      >
        <Sidebar onItemClick={handleDrawerToggle} />
      </Drawer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard;